import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  BookOpen,
  TreeDeciduous,
  Leaf,
  Bug,
  Droplets,
  Flame,
  Bird,
  ArrowRight,
  ExternalLink,
  GraduationCap,
  FileText,
  Video,
  Map,
  TreePine,
  ThermometerSun,
  Wind,
  Zap,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Sprout
} from 'lucide-react';

export default function EducationPage() {
  const { t, language } = useLanguage();
  const [showSoilDetails, setShowSoilDetails] = useState(false);

  // Build educational sections from translations
  // Note: Fire Risk is now merged with Soil Science in a combined section below
  const EDUCATION_SECTIONS = [
    {
      id: 'native-forest',
      title: t('education.nativeForest.title'),
      subtitle: t('education.nativeForest.subtitle'),
      icon: TreeDeciduous,
      color: 'bg-green-500/10 text-green-600',
      content: [
        t('education.nativeForest.lenga'),
        t('education.nativeForest.coihue'),
        t('education.nativeForest.cipres')
      ]
    },
    {
      id: 'invasive-species',
      title: t('education.invasiveSpecies.title'),
      subtitle: t('education.invasiveSpecies.subtitle'),
      icon: Bug,
      color: 'bg-red-500/10 text-red-600',
      content: [
        t('education.invasiveSpecies.pine'),
        t('education.invasiveSpecies.rosehip'),
        t('education.invasiveSpecies.broom')
      ]
    },
    {
      id: 'biodiversity',
      title: t('education.biodiversity.title'),
      subtitle: t('education.biodiversity.subtitle'),
      icon: Bird,
      color: 'bg-purple-500/10 text-purple-600',
      content: [
        t('education.biodiversity.fauna'),
        t('education.biodiversity.lifeNetwork'),
        t('education.biodiversity.corridors')
      ]
    }
  ];

  // Water cycle sections
  const WATER_CYCLE_SECTIONS = [
    { emoji: '☁️', ...t('education.waterCycle.whereRainComes') },
    { emoji: '🌳', ...t('education.waterCycle.treesBreathe') },
    { emoji: '🌱', ...t('education.waterCycle.whenRainFalls') },
    { emoji: '💧', ...t('education.waterCycle.forestsGiveMemory') },
    { emoji: '🔥', ...t('education.waterCycle.whenForestsBurn') },
    { emoji: '🏞️', ...t('education.waterCycle.canValleyHelp') },
    { emoji: '🌍', ...t('education.waterCycle.whyNativeSpecial') },
    { emoji: '🌲', ...t('education.waterCycle.forestsNotDecoration') }
  ];

  // Resources
  const RESOURCES = [
    {
      title: t('education.resources.guide.title'),
      type: t('education.resources.guide.type'),
      icon: FileText,
      description: t('education.resources.guide.description'),
      url: '#'
    },
    {
      title: t('education.resources.documentary.title'),
      type: t('education.resources.documentary.type'),
      icon: Video,
      description: t('education.resources.documentary.description'),
      url: '#'
    },
    {
      title: t('education.resources.map.title'),
      type: t('education.resources.map.type'),
      icon: Map,
      description: t('education.resources.map.description'),
      url: '/map'
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="education-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80"
            alt="Forest education"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 pt-16">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <GraduationCap className="w-3.5 h-3.5" />
              {t('education.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              {t('education.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('education.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 px-4 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {/* Fire Risk & Soil Science Combined Link */}
            <a
              href="#fire-soil-science"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 hover:border-orange-500/50 transition-colors"
            >
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium text-orange-600">
                {language === 'es' ? 'Fuego y Suelo' : 'Fire & Soil Science'}
              </span>
            </a>
            
            {/* Water Cycle Featured Link */}
            <a
              href="#water-cycle"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 hover:border-blue-500/50 transition-colors"
            >
              <Droplets className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-blue-600">{t('education.waterCycle.mainTitle')}</span>
            </a>
            
            {EDUCATION_SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-background border border-border hover:border-primary/50 transition-colors"
                >
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">{section.title}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Education Sections */}
      {EDUCATION_SECTIONS.map((section, sectionIndex) => {
        const Icon = section.icon;
        return (
          <section 
            key={section.id} 
            id={section.id}
            className={`py-16 px-4 ${sectionIndex % 2 === 0 ? 'bg-background' : 'bg-card'}`}
          >
            <div className="max-w-6xl mx-auto">
              <motion.div 
                className="mb-12"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-lg ${section.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-foreground">{section.title}</h2>
                    <p className="text-muted-foreground">{section.subtitle}</p>
                  </div>
                </div>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6">
                {section.content?.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="natural-card h-full overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-foreground">{item?.title}</h3>
                          {item?.impact && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              item.impact === 'Alto' || item.impact === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {item.impact}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item?.description}
                        </p>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Fire Risk & Soil Science Combined Section */}
      <section id="fire-soil-science" className="py-20 px-4 bg-gradient-to-b from-orange-950/20 via-amber-950/20 to-green-950/20">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/20 to-amber-500/20 text-orange-600 dark:text-orange-400 text-sm font-medium mb-4">
              <Flame className="w-4 h-4" />
              <span className="mx-1">+</span>
              <Leaf className="w-4 h-4" />
              {language === 'es' ? 'Riesgo de Incendios y Ciencia del Suelo' : 'Fire Risk & Soil Science'}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'es' 
                ? 'El Ciclo de Destrucción: Pinos, Suelo y Fuego' 
                : 'The Destruction Cycle: Pine, Soil & Fire'}
            </h2>
            <p className="text-xl text-orange-600 dark:text-orange-400 font-medium mb-2">
              {language === 'es'
                ? 'Cómo los pinos invasores transforman el suelo y multiplican el riesgo de incendios'
                : 'How invasive pines transform the soil and multiply fire risk'}
            </p>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'es'
                ? 'La diferencia no es solo el árbol. La diferencia es todo el sistema del suelo debajo — y cómo eso alimenta o detiene el fuego.'
                : 'The difference is not only the tree. The difference is the entire soil system underneath — and how that feeds or stops fire.'}
            </p>
          </motion.div>

          {/* Fire Risk Introduction Cards */}
          <motion.div
            className="grid md:grid-cols-3 gap-6 mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Why Pines Are Dangerous */}
            <Card className="p-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30">
              <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-bold text-foreground mb-2">
                {language === 'es' ? '¿Por qué los pinos son peligrosos?' : 'Why are pines dangerous?'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'es'
                  ? 'Los pinos contienen resinas altamente inflamables. Un bosque de pinos puede arder 10 veces más rápido que un bosque nativo. Sus copas densas facilitan la propagación del fuego de copa, el más destructivo.'
                  : 'Pines contain highly flammable resins. A pine forest can burn 10 times faster than a native forest. Their dense canopies facilitate crown fire spread, the most destructive type.'}
              </p>
            </Card>
            
            {/* Native Forest Role */}
            <Card className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center mb-4">
                <TreeDeciduous className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-foreground mb-2">
                {language === 'es' ? 'El rol del bosque nativo' : 'The role of native forest'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'es'
                  ? 'Los árboles nativos como la lenga tienen mayor contenido de humedad y menor cantidad de resinas. Un bosque nativo saludable actúa como cortafuego natural y se recupera mejor después de un incendio.'
                  : 'Native trees like lenga have higher moisture content and fewer resins. A healthy native forest acts as a natural firebreak and recovers better after a fire.'}
              </p>
            </Card>
            
            {/* Community Prevention */}
            <Card className="p-6 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/30">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center mb-4">
                <Wind className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-foreground mb-2">
                {language === 'es' ? 'Prevención comunitaria' : 'Community prevention'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'es'
                  ? 'La remoción de pinos, la creación de cortafuegos, y los sistemas de detección temprana son fundamentales. Cada árbol nativo plantado contribuye a reducir el riesgo.'
                  : 'Pine removal, firebreak creation, and early detection systems are essential. Every native tree planted contributes to reducing the risk.'}
              </p>
            </Card>
          </motion.div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 mb-16">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent"></div>
            <span className="text-muted-foreground text-sm font-medium">
              {language === 'es' ? 'La Ciencia Detrás del Riesgo' : 'The Science Behind the Risk'}
            </span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent"></div>
          </div>

          {/* Soil Comparison Sub-Header */}
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-foreground mb-2">
              {language === 'es' 
                ? 'Comparación del Suelo: Nativo vs Pino' 
                : 'Soil Comparison: Native vs Pine'}
            </h3>
            <p className="text-muted-foreground">
              {language === 'es'
                ? 'Entendiendo cómo el tipo de bosque transforma fundamentalmente el suelo'
                : 'Understanding how forest type fundamentally transforms the soil'}
            </p>
          </motion.div>

          {/* Main Comparison Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Native Forest Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30 overflow-hidden">
                <div className="bg-green-600 text-white p-4">
                  <div className="flex items-center gap-3">
                    <TreeDeciduous className="w-8 h-8" />
                    <div>
                      <h3 className="text-xl font-bold">
                        {language === 'es' ? 'Suelo de Bosque Nativo' : 'Native Forest Soil'}
                      </h3>
                      <p className="text-green-200 text-sm">coihue, lenga, ñire, radal</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-muted-foreground mb-4">
                    {language === 'es' 
                      ? 'Los bosques nativos patagónicos crean:'
                      : 'Native Patagonian forests create:'}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {(language === 'es' 
                      ? ['Alta diversidad orgánica', 'Capa profunda de humus', 'Redes fúngicas (micorrizas)', 'Retención constante de humedad', 'Estructura esponjosa', 'Alta actividad microbiana']
                      : ['High organic diversity', 'Deep humus layer', 'Fungal networks (mycorrhizae)', 'Constant moisture retention', 'Spongy structure', 'High microbial activity']
                    ).map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-green-500/10 rounded-lg p-4 text-center">
                      <Droplets className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-600">25–45%</p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'es' ? 'Humedad del suelo' : 'Soil moisture'}
                      </p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-4 text-center">
                      <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                      <p className="text-lg font-bold text-green-600">0.1–0.5 m/min</p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'es' ? 'Propagación' : 'Fire spread'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-green-500/20 rounded-lg p-4 text-center">
                    <p className="text-lg font-bold text-green-600">
                      {language === 'es' 
                        ? '🧽 Bosque nativo = esponja húmeda'
                        : '🧽 Native forest = damp sponge'}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Pine Plantation Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-gradient-to-br from-red-500/10 to-orange-500/10 border-red-500/30 overflow-hidden">
                <div className="bg-red-600 text-white p-4">
                  <div className="flex items-center gap-3">
                    <TreePine className="w-8 h-8" />
                    <div>
                      <h3 className="text-xl font-bold">
                        {language === 'es' ? 'Suelo de Plantación de Pino' : 'Pine Plantation Soil'}
                      </h3>
                      <p className="text-red-200 text-sm">
                        {language === 'es' ? 'El pino altera radicalmente el suelo' : 'Pine radically alters soil'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-muted-foreground mb-4">
                    {language === 'es' 
                      ? 'Características del suelo de pino:'
                      : 'Pine soil characteristics:'}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {(language === 'es' 
                      ? ['Hojarasca ácida (pH 3.5–4.5)', 'Agujas saturadas de resina', 'Pobre diversidad microbiana', 'Casi sin simbiosis fúngica', 'Suelo mineral compactado', 'Hidrofóbico (repele el agua)']
                      : ['Acidic litter (pH 3.5–4.5)', 'Resin-saturated needles', 'Poor microbial diversity', 'Almost no fungal symbiosis', 'Compacted mineral soil', 'Hydrophobic (repels water)']
                    ).map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-red-500/10 rounded-lg p-4 text-center">
                      <Droplets className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-red-600">5–12%</p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'es' ? 'Humedad del suelo' : 'Soil moisture'}
                      </p>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-4 text-center">
                      <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                      <p className="text-lg font-bold text-red-600">5–60 m/min</p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'es' ? 'Propagación' : 'Fire spread'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-red-500/20 rounded-lg p-4 text-center">
                    <p className="text-lg font-bold text-red-600">
                      {language === 'es' 
                        ? '🔥 Plantación de pino = alfombra de gasolina'
                        : '🔥 Pine plantation = gasoline carpet'}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* 100x Faster Callout */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-8 text-center text-white mb-12"
          >
            <p className="text-5xl md:text-7xl font-black mb-3">100×</p>
            <p className="text-xl md:text-2xl font-medium">
              {language === 'es' 
                ? 'El fuego en pino se propaga 100 veces más rápido'
                : 'Fire in pine spreads 100 times faster'}
            </p>
            <p className="text-white/70 mt-2">
              {language === 'es'
                ? 'Con viento: 40–60 metros por minuto'
                : 'In wind: 40–60 meters per minute'}
            </p>
          </motion.div>

          {/* Expand/Collapse for Details */}
          <div className="flex justify-center mb-8">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowSoilDetails(!showSoilDetails)}
              className="flex items-center gap-2"
            >
              {showSoilDetails 
                ? (language === 'es' ? 'Ocultar detalles científicos' : 'Hide scientific details')
                : (language === 'es' ? 'Ver análisis completo' : 'View full analysis')}
              {showSoilDetails ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </Button>
          </div>

          {/* Expandable Details */}
          <AnimatePresence>
            {showSoilDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-8 overflow-hidden"
              >
                {/* Comparison Table */}
                <Card className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    {language === 'es' ? 'Comparación Cuantificada' : 'Quantified Comparison'}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-border">
                          <th className="py-3 px-4 text-left font-bold text-foreground">
                            {language === 'es' ? 'Factor' : 'Factor'}
                          </th>
                          <th className="py-3 px-4 text-left font-bold text-green-600">
                            {language === 'es' ? 'Bosque Nativo' : 'Native Forest'}
                          </th>
                          <th className="py-3 px-4 text-left font-bold text-red-600">
                            {language === 'es' ? 'Plantación de Pino' : 'Pine Plantation'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(language === 'es' ? [
                          ['Humedad del suelo', '25–45%', '5–12%'],
                          ['Temp. ignición hojarasca', '450–500°C', '260–300°C'],
                          ['Velocidad propagación', '0.1–0.5 m/min', '5–60 m/min'],
                          ['Probabilidad fuego de copa', 'Muy baja', 'Extremadamente alta'],
                          ['Auto-extinción', 'Común', 'Rara'],
                          ['Recuperación del suelo', 'Rápida', 'Muy lenta'],
                          ['Riesgo de erosión', 'Bajo', 'Extremo']
                        ] : [
                          ['Soil moisture', '25–45%', '5–12%'],
                          ['Litter ignition temp', '450–500°C', '260–300°C'],
                          ['Fire spread speed', '0.1–0.5 m/min', '5–60 m/min'],
                          ['Crown fire probability', 'Very low', 'Extremely high'],
                          ['Self-extinguishing', 'Common', 'Rare'],
                          ['Soil recovery after fire', 'Fast', 'Very slow'],
                          ['Erosion risk', 'Low', 'Extreme']
                        ]).map((row, idx) => (
                          <tr key={idx} className="border-b border-border/50">
                            <td className="py-3 px-4 font-medium text-foreground">{row[0]}</td>
                            <td className="py-3 px-4 text-green-600">{row[1]}</td>
                            <td className="py-3 px-4 text-red-600">{row[2]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Fire Intensity */}
                <Card className="p-6 bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <ThermometerSun className="w-5 h-5 text-orange-500" />
                    {language === 'es' ? 'Comparación de Intensidad del Fuego' : 'Fire Intensity Comparison'}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {language === 'es' ? 'Intensidad del fuego (kW/m):' : 'Fire intensity (kW/m):'}
                  </p>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-green-500/10 rounded-xl p-6 text-center">
                      <TreeDeciduous className="w-10 h-10 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-1">
                        {language === 'es' ? 'Bosque Nativo' : 'Native Forest'}
                      </p>
                      <p className="text-3xl font-bold text-green-600">500–2,000 kW/m</p>
                    </div>
                    <div className="bg-red-500/10 rounded-xl p-6 text-center">
                      <TreePine className="w-10 h-10 text-red-600 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-1">
                        {language === 'es' ? 'Plantación de Pino' : 'Pine Plantation'}
                      </p>
                      <p className="text-3xl font-bold text-red-600">20,000–60,000 kW/m</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">
                      {language === 'es' ? 'Eso es 10–30 veces más energía.' : "That's 10–30 times more energy."}
                    </p>
                    <p className="text-red-600 font-medium mt-2">
                      {language === 'es' 
                        ? '⚠️ Ningún bombero puede estar cerca de eso.'
                        : '⚠️ No firefighter can stand near that.'}
                    </p>
                  </div>
                </Card>

                {/* Three Fire Ladders */}
                <Card className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    {language === 'es' 
                      ? '¿Por qué los incendios de pino se mueven tan rápido?'
                      : 'Why pine fires move so fast'}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {language === 'es'
                      ? 'Porque el pino crea tres escaleras de fuego a la vez:'
                      : 'Because pine creates three fire ladders at once:'}
                  </p>
                  <div className="grid md:grid-cols-3 gap-6">
                    {(language === 'es' ? [
                      { num: '1', title: 'Agujas de resina', subtitle: '(combustible de suelo)', points: ['Altamente inflamables', 'Ignición ≈ 260°C', 'Nativo: 450–500°C', '¡Casi 2× diferencia!'] },
                      { num: '2', title: 'Escalera vertical', subtitle: '(combustible)', points: ['Ramas del suelo a la copa', 'El fuego sube fácil', 'Nativos se auto-podan'] },
                      { num: '3', title: 'Continuidad de copas', subtitle: '', points: ['Copas uniformes', 'Fuego salta copa a copa', 'Nativo: irregular'] }
                    ] : [
                      { num: '1', title: 'Resin needles', subtitle: '(ground fuel)', points: ['Highly flammable', 'Ignition ≈ 260°C', 'Native: 450–500°C', 'Nearly 2× difference!'] },
                      { num: '2', title: 'Vertical ladder', subtitle: '(fuel)', points: ['Branches ground to crown', 'Fire climbs easily', 'Natives self-prune'] },
                      { num: '3', title: 'Crown continuity', subtitle: '', points: ['Uniform canopies', 'Fire jumps crown-to-crown', 'Native: irregular'] }
                    ]).map((ladder, idx) => (
                      <div key={idx} className="bg-amber-500/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="w-10 h-10 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center text-lg">
                            {ladder.num}
                          </span>
                          <div>
                            <h4 className="font-bold text-foreground">{ladder.title}</h4>
                            {ladder.subtitle && <p className="text-xs text-muted-foreground">{ladder.subtitle}</p>}
                          </div>
                        </div>
                        <ul className="space-y-2">
                          {ladder.points.map((point, pIdx) => (
                            <li key={pIdx} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <span className="text-amber-500">•</span>
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Water Cycle Difference */}
                <Card className="p-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-blue-500" />
                    {language === 'es' ? 'Diferencia del Ciclo del Agua (crítico)' : 'Water Cycle Difference (critical)'}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-bold text-green-600 mb-3">
                        {language === 'es' ? 'Suelo de bosque nativo:' : 'Native forest soil:'}
                      </h4>
                      <ul className="space-y-2">
                        {(language === 'es' 
                          ? ['Absorbe la lluvia', 'Almacena agua', 'Libera humedad lentamente', 'Enfría el microclima']
                          : ['Absorbs rain', 'Stores water', 'Releases moisture slowly', 'Cools microclimate']
                        ).map((point, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-foreground">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-red-600 mb-3">
                        {language === 'es' ? 'Suelo de pino:' : 'Pine soil:'}
                      </h4>
                      <ul className="space-y-2">
                        {(language === 'es' 
                          ? ['Repele la lluvia', 'Aumenta la escorrentía', 'Se seca rápidamente', 'Aumenta el calor']
                          : ['Repels rain', 'Runoff increases', 'Dries quickly', 'Increases heat']
                        ).map((point, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-foreground">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <p className="text-center text-muted-foreground mt-6 italic">
                    {language === 'es'
                      ? 'Esto crea microclimas calientes, secos y ventosos. El fuego se alimenta de eso.'
                      : 'This creates hot, dry, windy microclimates. Fire feeds on that.'}
                  </p>
                </Card>

                {/* Feedback Loop */}
                <Card className="p-6 bg-red-500/10 border-red-500/30">
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    {language === 'es' 
                      ? '¿Por qué los incendios "explotan" en áreas de pino?'
                      : 'Why fires "explode" in pine areas'}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {language === 'es' ? 'Porque los sistemas de pino:' : 'Because pine systems:'}
                  </p>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {(language === 'es' 
                      ? ['Se secan más rápido', 'Se encienden antes', 'Queman más caliente', 'Se propagan más rápido', 'Queman continuamente', 'Regeneran más pino después']
                      : ['Dry faster', 'Ignite earlier', 'Burn hotter', 'Spread faster', 'Burn continuously', 'Regenerate more pine afterward']
                    ).map((point, idx) => (
                      <span key={idx} className="px-4 py-2 bg-red-500/20 text-red-600 dark:text-red-400 rounded-full text-sm font-medium">
                        {point}
                      </span>
                    ))}
                  </div>
                  <p className="text-red-600 font-bold text-lg">
                    {language === 'es' ? '🔄 Es un ciclo de retroalimentación.' : "🔄 It's a feedback loop."}
                  </p>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Public Quote */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="my-12"
          >
            <Card className="border-2 border-amber-500 p-8 text-center">
              <p className="text-xl md:text-2xl font-bold text-foreground italic">
                &ldquo;{language === 'es' 
                  ? 'Los pinos no solo se queman — rediseñan el suelo para quemarse más rápido.'
                  : 'Pine trees do not only burn — they redesign the soil to burn faster.'}&rdquo;
              </p>
              <p className="text-muted-foreground mt-2">
                {language === 'es' ? '— Eso no es exageración — es física.' : "— That's not exaggeration — that's physics."}
              </p>
            </Card>
          </motion.div>

          {/* Why De-Pining Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30 p-8">
              <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Sprout className="w-8 h-8 text-green-600" />
                {language === 'es' ? 'Por qué funciona el despinado' : 'Why de-pining works'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {language === 'es' ? 'Remover el pino:' : 'Removing pine:'}
              </p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {(language === 'es' 
                  ? ['Aumenta la humedad del suelo', 'Restaura los hongos', 'Rompe la continuidad del combustible', 'Baja la altura de las llamas', 'Reduce la velocidad de propagación', 'Permite que las especies nativas regresen']
                  : ['Raises soil moisture', 'Restores fungi', 'Breaks fuel continuity', 'Lowers flame height', 'Slows fire spread', 'Allows native species to return']
                ).map((point, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-green-500/10 rounded-lg p-4">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-foreground font-medium">{point}</span>
                  </div>
                ))}
              </div>
              <p className="text-2xl font-bold text-green-600 text-center">
                {language === 'es' ? '✅ Por eso la prevención funciona.' : '✅ That\'s why prevention works.'}
              </p>
              
              <div className="mt-8 text-center">
                <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                  <Link to="/donate/depining">
                    <TreePine className="w-5 h-5 mr-2" />
                    {language === 'es' ? 'Apoyar el Despinado' : 'Support De-Pining'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Water Cycle Section */}
      <section id="water-cycle" className="py-20 px-4 bg-gradient-to-b from-blue-950 to-green-950 text-white">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm font-medium mb-6">
              <Droplets className="w-4 h-4" />
              {t('education.waterCycle.mainTitle')}
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              🌧️ {t('education.waterCycle.howRainWorks')}
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              {t('education.waterCycle.whyForests')}
            </p>
          </motion.div>

          {/* Introduction */}
          <motion.div 
            className="text-center mb-16 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-white/90 italic leading-relaxed">
              {t('education.waterCycle.intro1')}
            </p>
            <p className="text-lg text-white/90 mt-4 leading-relaxed">
              {t('education.waterCycle.intro2')}
            </p>
            <p className="text-white/60 mt-6">{t('education.waterCycle.letsSeee')}</p>
          </motion.div>

          {/* Water Cycle Cards */}
          <div className="space-y-8">
            {WATER_CYCLE_SECTIONS.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 overflow-hidden">
                  <div className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <span className="text-4xl">{section.emoji}</span>
                      <h3 className="text-2xl font-bold text-white">{section.title}</h3>
                    </div>
                    <div className="prose prose-invert prose-sm max-w-none">
                      <p className="text-white/80 leading-relaxed whitespace-pre-line">
                        {section.content}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Final Message */}
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border-white/20 p-10">
              <span className="text-5xl mb-6 block">💚</span>
              <h3 className="text-3xl font-bold text-white mb-4">{t('education.waterCycle.simpleTruth.title')}</h3>
              <p className="text-xl text-white/60 mb-6">{t('education.waterCycle.simpleTruth.noForest')}</p>
              <div className="text-lg text-white/80 space-y-2">
                <p>{t('education.waterCycle.simpleTruth.withForests')}</p>
                <p>• {t('education.waterCycle.simpleTruth.rainReturns')}</p>
                <p>• {t('education.waterCycle.simpleTruth.springsRecover')}</p>
                <p>• {t('education.waterCycle.simpleTruth.lakesSurvive')}</p>
                <p>• {t('education.waterCycle.simpleTruth.lifeContinues')}</p>
              </div>
              <p className="text-xl text-white mt-8 font-medium">
                {t('education.waterCycle.simpleTruth.protectForests')}
              </p>
              
              <div className="mt-10">
                <Button asChild size="lg" className="bg-white text-green-900 hover:bg-white/90">
                  <Link to="/adopt">
                    <TreeDeciduous className="w-5 h-5 mr-2" />
                    {t('education.waterCycle.simpleTruth.cta')}
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 px-4 eco-gradient text-white">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-2">{t('education.resources.title')}</h2>
            <p className="text-white/80">{t('education.resources.subtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {RESOURCES.map((resource, index) => {
              const Icon = resource.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-white/10 backdrop-blur-sm border-white/20 p-6 h-full hover:bg-white/20 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-xs uppercase tracking-wider text-white/60">{resource.type}</span>
                    </div>
                    <h3 className="font-semibold text-white mb-2">{resource.title}</h3>
                    <p className="text-sm text-white/70 mb-4">{resource.description}</p>
                    <Link to={resource.url} className="text-sm text-white/90 hover:text-white flex items-center gap-1">
                      {t('education.resources.viewResource')} <ExternalLink className="w-3 h-3" />
                    </Link>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-background">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">{t('education.cta.title')}</h2>
            <p className="text-muted-foreground mb-8">
              {t('education.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="btn-primary">
                <Link to="/adopt">
                  <TreeDeciduous className="w-5 h-5 mr-2" />
                  {t('landing.heroCta')}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/map">
                  <Map className="w-5 h-5 mr-2" />
                  {t('landing.heroExplore')}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 eco-gradient text-white/80">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Leaf className="w-5 h-5" />
              </div>
              <span className="font-semibold text-lg text-white">Epuyén Renace</span>
            </div>
            <p className="text-sm opacity-70">
              © {new Date().getFullYear()} Epuyén Renace. {t('landing.footerTagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
